/*
 *  			'License for ONE AXA SOFTWARE - Inner Source'
 * 					L O A S - I S
 *
 * The following License aims to be applied between the AXA Companies, according to the following definition:
 * "AXA Companies" means AXA, Société Anonyme with a “AXA Companies ” Board of Directors having its principal offices at 25, avenue Matignon, 75008 Paris, registered on the Commercial Registry of Paris under the number 572 093 920; and (i) any other company controlled by, or controlling AXA, with a company being considered as controlling another: (a) when it holds directly or indirectly a portion of the capital according to it the majority of the voting rights in general meetings of shareholders of this company; (b) when it holds solely the majority of the voting rights in this company by virtue of an agreement concluded with other partners or shareholders and which is not contrary to the interest of the company; (c) when it determines de facto, by voting rights which it holds, the decisions in the general meetings of shareholders of this company; (d) in any event, when it holds, directly or indirectly, a portion of voting rights greater than 40% and when no other partner or shareholder holds directly or indirectly a portion which is greater than its own; (ii) any economic interest group in which AXA and/or one or more other Companies of the AXA Group participates for at least 50% in operating costs; (iii) in the cases where the law applicable to a company limits voting rights or control (such as defined here in above), this company will be deemed to be a company of the AXA Group, if the voting rights in 2 Binding Corporate Rules – AXA Group – version 2013 general shareholders’ meetings or the control held by a Company of the AXA Group reaches the maximum amount fixed by said applicable law; and (iv) all AXA Companies constitute the “AXA Group” (hereafter "One AXA").  (Binding Corporate Rules - AXA Group - version 2019).
 *
 * Software credits:
 * 	Creator: AXA GO GETD
 * 	Main contributors: AXA GO GETD
 *
 * Permission is hereby granted, free of charge, to any person or entity within One AXA obtaining a copy of this software and associated documentation files (the "Software"), to deal in the Software within One AXA, including the rights to use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the Software, and to permit persons to whom the Software is furnished to do so, subject to the following conditions:
 *
 * The above credit notice and this permission notice shall be included in all copies or substantial portions of the Software.
 * Any improvement or modification of the Software shall be made available to the whole One AXA, under the terms of this License.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */

import React, { useContext, useEffect, useRef, useState } from 'react';
import AdaptiveCard from 'react-adaptivecards';

import { resetConversation } from '@store/reducers/conversations';
import { formatNumber } from '@utils/numberUtils';
import { MESSAGES } from '@translations/labels';
import SVGIconClose from '@svg/IconClose';
import SVGIconChatbotBot from '@svg/IconChatbotBot';
import SVGIconChatbotUser from '@svg/IconChatbotUser';
import { NONE_INTENT } from '@constants';
import { Modal } from 'antd';
import { useDispatch } from 'react-redux';
import { SocketContext } from 'commons/contexts/SocketContext';
import { leaveConversation } from '@components/websocket/webSocketAPI';
import { AppContext } from 'commons/contexts/AppContext';
import ConversationLiveButton from './conversationLiveButton';

const labels = { ...MESSAGES.commons, ...MESSAGES.conversationHistory };

const extractTextFromCard = sentence => {
  let text;
  if (sentence && sentence.attachments) {
    const attachments = JSON.parse(sentence.attachments);
    if (attachments.length) {
      text = attachments.map((at, index) => {
        return <AdaptiveCard payload={at.content} key={`ac-${index}`} />;
      });
    }
  }

  if (sentence.value) {
    text = JSON.stringify(sentence.value);
  }

  return text;
};

const isUser = sentence => sentence?.from?.role === 'user';

const ConversationDetailsModal = ({ conversationDetail, isRemoteDataSource }) => {
  const dispatch = useDispatch();
  const bottomRef = useRef(null);
  const { socket } = useContext(SocketContext);
  const { axaConfig } = useContext(AppContext);
  const [isVisible, setIsVisible] = useState(true);
  const [isLive, setIsLive] = useState(false);
  const { data, context } = conversationDetail;

  const scrollToBottom = () => bottomRef.current?.scrollIntoView({ behavior: 'smooth' });

  useEffect(() => {
    if (isLive && conversationDetail) scrollToBottom();
  }, [conversationDetail?.data.length]);

  const handleClose = () => {
    dispatch(resetConversation());
    setIsVisible(false);
    setIsLive(false);
    leaveConversation(socket, context.conversationId);
  };

  const formattedSentences = data.map(sentence => {
    return {
      ...sentence,
      isUser: isUser(sentence),
    };
  });
  return (
    <Modal
      className="conversation-detail-modal-content"
      closable={false}
      key={context.id}
      visible={isVisible}
      footer={null}
      width="560px"
    >
      <>
        <header className="header-container">
          <SVGIconClose className="close-icon" onClick={handleClose} />
          <h4 className="modal-title">
            {labels.modalTitle} {isRemoteDataSource ? '(chatbot)' : '(NLP test)'}{' '}
            {isRemoteDataSource && axaConfig?.webSocketOn && (
              <ConversationLiveButton
                isLive={isLive}
                setIsLive={setIsLive}
                socket={socket}
                context={context}
                isRemoteDataSource={isRemoteDataSource}
              />
            )}
          </h4>
          <p className="modal-subtitle">
            <strong>{context.message}</strong>
          </p>
          {context.intent && (
            <p className="modal-answer-subtitle">
              {context.intent}, {context.score}
            </p>
          )}
        </header>
        <div className="conversation">
          <ul className="no-margin-padding">
            {formattedSentences.map((sentence, i) => (
              <li className={`message ${!sentence.isUser ? 'bot row' : 'user row-reverse'} flex `} key={`message-${i}`}>
                <div className="message-container">
                  <div className={`box-message ${!sentence.isUser ? 'bot row' : 'user row-reverse'} flex align-middle`}>
                    {!sentence.isUser ? (
                      <SVGIconChatbotBot className="icon bot" />
                    ) : (
                      <SVGIconChatbotUser className="icon user" />
                    )}
                    <p className="text">{sentence.text || extractTextFromCard(sentence) || sentence.name || 'empty'}</p>
                  </div>
                  {sentence.nlp && (
                    <p className="intent-score">
                      {sentence?.nlp?.intent?.toLowerCase() === NONE_INTENT ? (
                        <>{sentence.nlp.intent}</>
                      ) : (
                        <span className="intent">
                          <a href={`/manage-the-bot/intents/${sentence.nlp.intent}/name?locale=${sentence.nlp.locale}`}>
                            {sentence.nlp.intent}
                          </a>
                        </span>
                      )}

                      <span>-</span>
                      {sentence?.nlp?.score && (
                        <span className="score">
                          {`${labels.score}: `}
                          {formatNumber(sentence.nlp.score)}
                        </span>
                      )}
                    </p>
                  )}
                  {sentence.isUser && !sentence.nlp && sentence?.textConfidence && (
                    <>{`${labels.confidence?.toLowerCase()}: ${
                      typeof sentence?.textConfidence === 'number'
                        ? formatNumber(sentence.textConfidence)
                        : sentence.textConfidence
                    }`}</>
                  )}
                  <p
                    className="who"
                    style={{
                      textAlign: `${!sentence.isUser ? 'left' : 'right'}`,
                    }}
                  >
                    {!sentence.isUser ? 'bot' : 'user'}
                  </p>
                </div>
              </li>
            ))}
            <li className="message" ref={bottomRef}>
              {labels.endConversation}
            </li>
          </ul>
        </div>
      </>
    </Modal>
  );
};

export default ConversationDetailsModal;
